<script lang="ts">
  import { goto } from '$app/navigation';
  import { t as __t, getLocale, setLocale, onLocaleChange } from '$lib/i18n';
  import { FEATURE_FLAGS } from '$lib/firebase/feature-flags';
  import { updateDocument, queryCollection } from '$lib/firebase/firestore';

  let {
    user = null,
    loggedIn = false,
    onLogout,
    isOpen = $bindable(false),
  }: {
    user?: any;
    loggedIn?: boolean;
    onLogout?: () => void;
    isOpen?: boolean;
  } = $props();

  // Reactive locale for template reactivity
  let _lcv = $state(0);
  let currentLocale = $state(getLocale());

  $effect(() => {
    const unsub = onLocaleChange(() => {
      currentLocale = getLocale();
      _lcv++;
    });
    return unsub;
  });

  function t(key: string): string {
    _lcv;
    return __t(key);
  }

  const locales = ['nl', 'en', 'de'];

  function toggleMenu() {
    isOpen = !isOpen;
  }

  function closeMenu() {
    isOpen = false;
  }



  function handleNavigate(path: string) {
    if (path === '/portal' && user && !user.hasFeature('PORTAL_V2')) {
      window.location.href = 'http://portaal.universalvoice.eu/';
    } else {
      goto(path);
    }
    closeMenu();
  }

  function handleLogoutClick() {
    onLogout?.();
    closeMenu();
  }

  function handleLocaleChange(e: Event) {
    const select = e.target as HTMLSelectElement;
    const locale = select.value;
    setLocale(locale);
    if (user?.uid) {
      updateDocument('users', { lang: locale }, user.uid).catch((err) => {
        console.error('Failed to save language preference:', err);
      });
    }
  }

  let canAdminOrg = $state(false);

  $effect(() => {
    if (loggedIn && user?.hasFeature?.(FEATURE_FLAGS.ORGANIZATION_USE) && user?.uid) {
      queryCollection<any>('organizations', [
        { field: 'adminUids', op: 'array-contains', value: user.uid }
      ]).then((snap) => {
        if (!snap.empty) {
          canAdminOrg = true;
        } else {
          // Fallback check for legacy adminUid field
          queryCollection<any>('organizations', [
            { field: 'adminUid', op: '==', value: user.uid }
          ]).then((legacySnap) => {
            canAdminOrg = !legacySnap.empty;
          });
        }
      }).catch((err) => {
        console.error('Failed to check if user administers organizations:', err);
      });
    } else {
      canAdminOrg = false;
    }
  });

  // Body scroll is managed by AppLayout's scroll container,
  // so no need to lock body overflow here.
</script>

<div class="relative z-50 flex items-center shrink-0">
  <!-- Hamburger Button -->
  <button
    onclick={toggleMenu}
    aria-label="Menu"
    class="bg-transparent border-none text-white text-[1.4rem] md:text-[1.6rem] p-2 md:p-3 cursor-pointer relative h-[60px] w-11 md:w-12 min-w-11 md:min-w-12 flex items-center justify-center shrink-0"
  >
    <span class:hidden={isOpen}>
      <i class="fas fa-bars"></i>
    </span>
    <span class:hidden={!isOpen}>
      <i class="fas fa-times"></i>
    </span>
  </button>

  <!-- Menu Overlay -->
  {#if isOpen}
    <!-- svelte-ignore a11y_click_events_have_key_events -->
    <div
      class="fixed inset-0 bg-black/50 z-[48]"
      style="padding-top: env(safe-area-inset-top, 0px); padding-bottom: env(safe-area-inset-bottom, 0px);"
      onclick={closeMenu}
    ></div>
  {/if}

  <nav
    class="fixed top-0 right-[-350px] md:right-[-350px] right-[-300px] w-[320px] md:w-[320px] w-[280px] h-dvh bg-black border-l-2 border-primary-500 transition-[right] duration-300 ease z-50 overflow-y-auto"
    class:is-open={isOpen}
    style="padding-top: env(safe-area-inset-top, 0px); padding-bottom: env(safe-area-inset-bottom, 0px); right: {isOpen ? '0' : undefined}"
  >
    <div class="flex justify-between items-center p-5 border-b border-gray-800">
      <h3 class="text-white m-0 text-xl font-light">{t('menu.menu')}</h3>
      <button
        onclick={closeMenu}
        class="bg-none border-none text-white text-lg cursor-pointer p-2"
      >
        <i class="fas fa-times"></i>
      </button>
    </div>

    <div class="py-5">
      <!-- Language Selector -->
      <div class="mb-8 px-5">
        <label class="block text-white text-sm font-medium mb-2.5 uppercase tracking-wider">{t('menu.language')}</label>
        <select
          value={currentLocale}
          onchange={handleLocaleChange}
          class="w-full bg-gray-900 border border-gray-700 text-white px-3.5 py-2.5 rounded text-sm"
        >
          {#each locales as l}
            <option value={l}>{l.toUpperCase()}</option>
          {/each}
        </select>
      </div>

      <!-- Main Navigation -->
      <div class="mb-8 px-5">
        <button
          class="flex items-center w-full bg-none border-none text-gray-300 px-5 py-3.5 text-lg text-left cursor-pointer transition-all duration-200 hover:bg-gray-900 hover:text-primary-500 -mx-5 relative uppercase"
          onclick={() => handleNavigate('/faq')}
        >
          <i class="fas fa-question-circle mr-3.5 w-5 text-base"></i>
          {t('menu.faq')}
        </button>

        {#if loggedIn}
          <!-- Divider -->
          <div class="border-t border-neutral-800 my-4 -mx-5"></div>

          <!-- Section 2: Tab Bar Actions -->
          <button
            class="flex items-center w-full bg-none border-none text-gray-300 px-5 py-3.5 text-lg text-left cursor-pointer transition-all duration-200 hover:bg-gray-900 hover:text-primary-500 -mx-5 relative uppercase"
            onclick={() => handleNavigate('/portal')}
          >
            <i class="fas fa-book mr-3.5 w-5 text-base"></i>
            {t('menu.portal_lower')}
          </button>

          <button
            class="flex items-center w-full bg-none border-none text-gray-300 px-5 py-3.5 text-lg text-left cursor-pointer transition-all duration-200 hover:bg-gray-900 hover:text-primary-500 -mx-5 relative uppercase"
            onclick={() => handleNavigate('/voice-plans')}
          >
            <i class="fas fa-microphone mr-3.5 w-5 text-base"></i>
            {t('menu.voicePlans')}
          </button>

          <button
            class="flex items-center w-full bg-none border-none text-gray-300 px-5 py-3.5 text-lg text-left cursor-pointer transition-all duration-200 hover:bg-gray-900 hover:text-primary-500 -mx-5 relative uppercase"
            onclick={() => handleNavigate('/playlists')}
          >
            <i class="fas fa-music mr-3.5 w-5 text-base"></i>
            {t('menu.playlist')}
          </button>

          <button
            class="flex items-center w-full bg-none border-none text-gray-300 px-5 py-3.5 text-lg text-left cursor-pointer transition-all duration-200 hover:bg-gray-900 hover:text-primary-500 -mx-5 relative uppercase"
            onclick={() => handleNavigate('/digital-guide')}
          >
            <i class="fas fa-university mr-3.5 w-5 text-base"></i>
            {t('menu.book')}
          </button>

          <!-- Divider -->
          <div class="border-t border-neutral-800 my-4 -mx-5"></div>

          <!-- Section 3: Account & Admin Actions -->
          <button
            class="flex items-center w-full bg-none border-none text-gray-300 px-5 py-3.5 text-lg text-left cursor-pointer transition-all duration-200 hover:bg-gray-900 hover:text-primary-500 -mx-5 relative uppercase"
            onclick={() => handleNavigate('/invitations')}
          >
            <i class="fas fa-envelope mr-3.5 w-5 text-base"></i>
            {t('menu.invitations')}
          </button>

          <button
            class="flex items-center w-full bg-none border-none text-gray-300 px-5 py-3.5 text-lg text-left cursor-pointer transition-all duration-200 hover:bg-gray-900 hover:text-primary-500 -mx-5 relative uppercase"
            onclick={() => handleNavigate('/settings')}
          >
            <i class="fas fa-cog mr-3.5 w-5 text-base"></i>
            {t('menu.account_and_subscription')}
          </button>

          {#if user?.hasFeature?.(FEATURE_FLAGS.ORGANIZATION_USE) && canAdminOrg}
            <button
              class="flex items-center w-full bg-none border-none text-gray-300 px-5 py-3.5 text-lg text-left cursor-pointer transition-all duration-200 hover:bg-gray-900 hover:text-primary-500 -mx-5 relative uppercase"
              onclick={() => handleNavigate('/organization')}
            >
              <i class="fas fa-users-cog mr-3.5 w-5 text-base"></i>
              {t('menu.organization')}
            </button>
          {/if}

          <!-- Admin and Special Features -->


          {#if user?.hasFeature?.(FEATURE_FLAGS.ADMIN)}
            <button
              class="flex items-center w-full bg-none border-none text-gray-300 px-5 py-3.5 text-lg text-left cursor-pointer transition-all duration-200 hover:bg-gray-900 hover:text-primary-500 -mx-5 relative uppercase"
              onclick={() => handleNavigate('/admin')}
            >
              <i class="fas fa-shield-alt mr-3.5 w-5 text-base"></i>
              ADMIN
            </button>
          {/if}
        {/if}
      </div>

      <!-- Logout -->
      {#if loggedIn}
        <div class="px-5">
          <button
            class="flex items-center w-full bg-none border-none text-red-400 px-5 py-3.5 text-lg text-left cursor-pointer transition-all duration-200 hover:bg-red-900/50 hover:text-red-300 -mx-5 relative border-t border-gray-800 pt-5 mt-5"
            onclick={handleLogoutClick}
          >
            <i class="fas fa-sign-out-alt mr-3.5 w-5 text-base"></i>
            {t('common.logout')}
          </button>
        </div>
      {/if}
    </div>
  </nav>
</div>

<style>
  .is-open {
    right: 0 !important;
  }
</style>
